"use client";

import React, {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { toast } from "react-toastify";

import { useInactivityTimer } from "@/hooks/useInactivityTimer";
import { useWorkspace as useWorkspaceAPI, useWorkspaceProfile } from "@/lib/orchestrator/workspaces/api";
import {
  WorkspaceDetailSerializer,
  WorkspaceListSerializer,
  WorkspaceUserProfile
} from "@/lib/orchestrator/workspaces/types";
import { WorkspaceContextType, WorkspaceSession } from "@/types";
import { WorkspaceSessionManager } from "@/utils/workspace-session";

// Constants
const CONSTANTS = {
  INACTIVITY_TIMEOUT: 30 * 60 * 1000,
  PROFILE_REFRESH_INTERVAL: 5 * 60 * 1000,
  MAX_RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
  BASE_APP_PATH: "/app"
} as const;

// Type definitions
type PinVerificationStatus = "idle" | "verifying" | "success" | "error";

interface WorkspaceState {
  workspaces: WorkspaceListSerializer[];
  currentWorkspace: WorkspaceDetailSerializer | null;
  workspaceProfile: WorkspaceUserProfile | null;
  showWorkspaceSelector: boolean;
  pinVerificationStatus: PinVerificationStatus;
  isInitializing: boolean;
  error: Error | null;
}

const initialState: WorkspaceState = {
  workspaces: [],
  currentWorkspace: null,
  workspaceProfile: null,
  showWorkspaceSelector: false,
  pinVerificationStatus: "idle",
  isInitializing: true,
  error: null
};

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

// Error handling utility
class WorkspaceError extends Error {
  constructor(message: string, public readonly critical: boolean = false) {
    super(message);
    this.name = "WorkspaceError";
  }
}

interface WorkspaceProviderProps extends PropsWithChildren {
  inactivityTimeout?: number;
  enableAutoRefresh?: boolean;
}

export function WorkspaceProvider({
  children,
  inactivityTimeout = CONSTANTS.INACTIVITY_TIMEOUT,
  enableAutoRefresh = true
}: WorkspaceProviderProps) {
  const [state, setState] = useState<WorkspaceState>(initialState);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const retryAttemptsRef = useRef<Record<string, number>>({});
  const profileRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializedRef = useRef(false);
  const stateRef = useRef(state);
  const workspaceLoadingRef = useRef(false);
  const params = new URLSearchParams(searchParams);

  // Add debug flag to track context operations
  const debugModeRef = useRef(true);
  const debugLog = (...args) => {
    if (debugModeRef.current) {
      console.log("[WorkspaceContext]", ...args);
    }
  };

  const {
    isLoading: isLoadingWorkspaces,
    error: workspaceError,
    getWorkspaces: getWorkspacesAPI,
    getWorkspace,
    verifyPIN,
    createWorkspace: createWorkspaceAPI
  } = useWorkspaceAPI();

  const {
    getCurrentProfile,
  } = useWorkspaceProfile();

  // Use useRef to track state updates
  useEffect(() => {
    stateRef.current = state;
    debugLog("State updated:", {
      hasWorkspace: !!state.currentWorkspace,
      workspaceId: state.currentWorkspace?.id,
      isInitializing: state.isInitializing
    });
  }, [state]);

  const handleAuthError = (error: any) => {
    // If token expired or authentication error
    if (error?.message?.includes("Token expired") ||
        error?.response?.status === 401) {

      debugLog("Auth error detected, redirecting to login");

      // Only redirect if we're not already on a login page
      if (typeof window !== "undefined" &&
          !window.location.pathname.includes(`/api/auth/login?${params.toString()}`) &&
          !window.location.pathname.includes(`/api/auth/logout?${params.toString()}`)) {

        // Use timeout to avoid immediate redirect conflicts
        setTimeout(() => {
          window.location.href = `/api/auth/login?${params.toString()}`;
        }, 100);
      }
      return true;
    }
    return false;
  };

  const updateState = useCallback((updates: Partial<WorkspaceState>) => {
    setState(prev => {
      const newState = { ...prev, ...updates };
      // Return new state only if actually changed
      return JSON.stringify(newState) !== JSON.stringify(prev) ? newState : prev;
    });
  }, []);

  const handleError = useCallback((error: Error | WorkspaceError, message: string) => {
    const isCritical = error instanceof WorkspaceError && error.critical;
    console.error(`[Workspace Error] ${message}:`, error);

    updateState({ error });

    // Show toast for critical errors
    if (isCritical) {
      toast.error(message);
    }
  }, [updateState]);

  const retryOperation = useCallback(async <T,>(
    operation: () => Promise<T>,
    errorMessage: string,
    operationKey: string
  ): Promise<T | null> => {
    const attempts = retryAttemptsRef.current[operationKey] || 0;

    try {
      const result = await operation();
      delete retryAttemptsRef.current[operationKey];
      return result;
    } catch (error) {
      retryAttemptsRef.current[operationKey] = attempts + 1;
      debugLog(`Retry attempt ${attempts + 1}/${CONSTANTS.MAX_RETRY_ATTEMPTS} for ${operationKey}`);

      if (attempts < CONSTANTS.MAX_RETRY_ATTEMPTS) {
        await new Promise(resolve => setTimeout(resolve, CONSTANTS.RETRY_DELAY * (attempts + 1)));
        return retryOperation(operation, errorMessage, operationKey);
      } else {
        delete retryAttemptsRef.current[operationKey];
        handleError(
          new WorkspaceError(errorMessage, true),
          errorMessage
        );
        return null;
      }
    }
  }, [handleError]);

  // Expose getWorkspaces function that updates the state
  const handleGetWorkspaces = useCallback(async () => {
    debugLog("Getting workspaces");
    try {
      const workspacesData = await retryOperation(
        () => getWorkspacesAPI(),
        "Failed to load workspaces",
        "get-workspaces"
      );

      if (!workspacesData?.data && !workspacesData) {
        debugLog("No workspaces data returned");
        return [];
      }

      // Extract and update workspaces list
      const workspacesList = workspacesData?.data || workspacesData || [];
      debugLog(`Got ${workspacesList.length} workspaces`);
      updateState({ workspaces: workspacesList });

      return workspacesList;
    } catch (error) {
      handleError(error as Error, "Failed to load workspaces");
      return [];
    }
  }, [getWorkspacesAPI, retryOperation, updateState, handleError]);

  const handleClearWorkspace = useCallback(() => {
    debugLog("Clearing workspace");
    if (profileRefreshTimerRef.current) {
      clearInterval(profileRefreshTimerRef.current);
      profileRefreshTimerRef.current = null;
    }

    updateState({
      currentWorkspace: null,
      workspaceProfile: null,
      pinVerificationStatus: "idle",
      error: null,
      showWorkspaceSelector: true
    });

    WorkspaceSessionManager.clearSession();
    if (pathname !== CONSTANTS.BASE_APP_PATH) {
      router.push(CONSTANTS.BASE_APP_PATH);
    }
  }, [updateState, router, pathname]);

  const setupProfileRefreshTimer = useCallback((workspaceId: string) => {
    if (!enableAutoRefresh) return;

    if (profileRefreshTimerRef.current) {
      clearInterval(profileRefreshTimerRef.current);
    }

    const refreshProfile = async () => {
      try {
        debugLog(`Refreshing profile for workspace ${workspaceId}`);
        const profileResponse = await getCurrentProfile(workspaceId);
        if (profileResponse?.data) {
          updateState({ workspaceProfile: profileResponse.data });
        }
      } catch (error) {
        console.warn("Failed to refresh workspace profile:", error);
      }
    };

    profileRefreshTimerRef.current = setInterval(
      refreshProfile,
      CONSTANTS.PROFILE_REFRESH_INTERVAL
    );

    return () => {
      if (profileRefreshTimerRef.current) {
        clearInterval(profileRefreshTimerRef.current);
      }
    };
  }, [enableAutoRefresh, getCurrentProfile, updateState]);

  const handleRefreshSession = useCallback(() => {
    const currentWorkspaceId = stateRef.current.currentWorkspace?.id;
    if (currentWorkspaceId) {
      WorkspaceSessionManager.updateActivity();
    }
  }, []);

  const handleVerifyPin = useCallback(async (
    workspaceId: string,
    pin: string
  ): Promise<boolean> => {
    updateState({ pinVerificationStatus: "verifying" });
    debugLog(`Verifying PIN for workspace ${workspaceId}`);

    try {
      const response = await retryOperation(
        () => verifyPIN(workspaceId, { pin }),
        "PIN verification failed",
        `verify-pin-${workspaceId}`
      );

      const isValid = response?.data || false;
      updateState({ pinVerificationStatus: isValid ? "success" : "error" });
      debugLog(`PIN verification ${isValid ? 'succeeded' : 'failed'}`);

      if (!isValid) {
        const message = typeof response === "object" && response && "message" in response
          ? String(response.message)
          : "Invalid PIN";
        toast.error(message);
      }

      return isValid;
    } catch {
      updateState({ pinVerificationStatus: "error" });
      return false;
    }
  }, [updateState, verifyPIN, retryOperation]);

  const handleSetWorkspace = useCallback(async (
    workspaceId: string,
    verifySecurityPin = true,
    preservePath = false
  ): Promise<void> => {
    debugLog(`Setting workspace ${workspaceId}, verifyPin=${verifySecurityPin}, preservePath=${preservePath}`);

    // Prevent multiple simultaneous attempts to set workspace
    if (workspaceLoadingRef.current) {
      debugLog("Workspace loading already in progress, ignoring");
      return;
    }

    workspaceLoadingRef.current = true;

    try {
      // Check if the workspace is already set
      if (stateRef.current.currentWorkspace?.id === workspaceId) {
        debugLog("Workspace already set, just closing selector");
        updateState({ showWorkspaceSelector: false });
        workspaceLoadingRef.current = false;
        return;
      }

      // Fetch workspace details and profile in parallel
      debugLog("Fetching workspace details and profile");
      const [workspaceResponse, profileResponse] = await Promise.all([
        retryOperation(
          () => getWorkspace(workspaceId),
          "Failed to load workspace",
          `get-workspace-${workspaceId}`
        ),
        retryOperation(
          () => getCurrentProfile(workspaceId),
          "Failed to load workspace profile",
          `get-profile-${workspaceId}`
        )
      ]);

      if (!workspaceResponse) {
        throw new WorkspaceError("Workspace not found", true);
      }

      // Handle pin verification requirement
      if (verifySecurityPin && profileResponse?.data?.security_status?.has_pin) {
        debugLog("PIN required for workspace, showing selector");
        updateState({ showWorkspaceSelector: true });
        workspaceLoadingRef.current = false;
        return;
      }

      // Extract data from responses safely
      const workspaceData = workspaceResponse.data || workspaceResponse;
      const profileData = profileResponse?.data || null;

      debugLog("Updating state with workspace data");
      updateState({
        currentWorkspace: workspaceData,
        workspaceProfile: profileData,
        showWorkspaceSelector: false,
        pinVerificationStatus: "idle",
        error: null
      });

      WorkspaceSessionManager.setSession(workspaceId);
      setupProfileRefreshTimer(workspaceId);

      // Only navigate if preservePath is false
      if (!preservePath) {
        const targetRoute = `/app/${workspaceId}`;
        debugLog(`Navigating to ${targetRoute}`);
        router.push(targetRoute);
      }
    } catch (error) {
      if (handleAuthError(error)) {
        workspaceLoadingRef.current = false;
        return;
      }

      handleError(error as Error, "Failed to set workspace");
      handleClearWorkspace();
    } finally {
      workspaceLoadingRef.current = false;
    }
  }, [
    getWorkspace,
    getCurrentProfile,
    updateState,
    handleError,
    retryOperation,
    setupProfileRefreshTimer,
    router,
    handleClearWorkspace
  ]);

  // Create workspace implementation
  const handleCreateWorkspace = useCallback(async (workspaceData: Partial<any>) => {
    debugLog("Creating new workspace");
    try {
      const response = await retryOperation(
        () => createWorkspaceAPI(workspaceData),
        "Failed to create workspace",
        "create-workspace"
      );

      if (!response) {
        throw new WorkspaceError("Couldn't create workspace", true);
      }

      // Extract workspace data from response
      const newWorkspace = response.data || response;
      debugLog("Workspace created successfully", newWorkspace);

      // Update workspaces list
      updateState({
        workspaces: [...stateRef.current.workspaces, newWorkspace]
      });

      // Optionally switch to the new workspace
      await handleSetWorkspace(newWorkspace.id, false);

      toast.success(`Workspace ${newWorkspace.name} created successfully`);
      return newWorkspace;
    } catch (error) {
      handleError(error as Error, "Failed to create workspace");
      throw error;
    }
  }, [createWorkspaceAPI, retryOperation, updateState, handleSetWorkspace, handleError]);

  // Extract workspaceId from URL path
  const extractWorkspaceIdFromPath = useCallback((path: string): string | null => {
    const matches = path.match(/\/app\/([^\/]+)/);
    return matches && matches[1] ? matches[1] : null;
  }, []);

  // Simple function to toggle workspace selector
  const handleToggleWorkspaceSelector = useCallback((show: boolean) => {
    // If we're opening the selector from the header button, and closing it when selecting a workspace
    updateState({ showWorkspaceSelector: show });
  }, [updateState]);

  // Handle workspace initialization
  useEffect(() => {
    if (isInitializedRef.current) return;

    const initializeWorkspaces = async () => {
      debugLog("Initializing workspaces");
      try {
        // Load all workspaces first
        await handleGetWorkspaces();

        // Check for URL workspace ID
        const urlWorkspaceId = extractWorkspaceIdFromPath(pathname);
        const existingSession = WorkspaceSessionManager.getSession();

        debugLog("Initialization context:", {
          urlWorkspaceId,
          hasExistingSession: !!existingSession
        });

        // Priority 1: URL workspace ID
        if (urlWorkspaceId) {
          debugLog("Setting workspace from URL", urlWorkspaceId);
          await handleSetWorkspace(urlWorkspaceId, true, true);
          isInitializedRef.current = true;
        }
        // Priority 2: Existing session
        else if (existingSession && isValidSession(existingSession)) {
          debugLog("Setting workspace from session", existingSession.workspaceId);
          await handleSetWorkspace(existingSession.workspaceId, false, false);
          isInitializedRef.current = true;
        }
        // Priority 3: On '/app' with no selection, show selector
        else {
          debugLog("No workspace available, showing selector");
          updateState({ showWorkspaceSelector: true });
          isInitializedRef.current = true;
        }
      } catch (error) {
        if (handleAuthError(error)) {
          return;
        }
        handleError(error as Error, "Failed to initialize workspaces");
        handleClearWorkspace();
      } finally {
        updateState({ isInitializing: false });
      }
    };

    initializeWorkspaces();
  }, [
    handleGetWorkspaces,
    handleSetWorkspace,
    handleError,
    updateState,
    pathname,
    handleClearWorkspace,
    extractWorkspaceIdFromPath
  ]);

  // Ensure workspace selector is shown on /app route if no workspace is selected
  useEffect(() => {
    if (pathname === CONSTANTS.BASE_APP_PATH && !state.currentWorkspace && !state.isInitializing) {
      debugLog("On /app with no workspace selected, showing selector");
      updateState({ showWorkspaceSelector: true });
    }
  }, [pathname, state.currentWorkspace, state.isInitializing, updateState]);

  // Handle inactivity
  useInactivityTimer(handleClearWorkspace, inactivityTimeout);

  // Add debug log on workspace change
  useEffect(() => {
    if (state.currentWorkspace) {
      debugLog("Current workspace changed:", {
        id: state.currentWorkspace.id,
        name: state.currentWorkspace.name
      });
    }
  }, [state.currentWorkspace]);

  const contextValue = useMemo<WorkspaceContextType>(() => ({
    workspaces: state.workspaces,
    currentWorkspace: state.currentWorkspace,
    workspaceProfile: state.workspaceProfile,
    isLoading: isLoadingWorkspaces || state.isInitializing,
    error: state.error || workspaceError,
    pinVerificationStatus: state.pinVerificationStatus,
    showWorkspaceSelector: state.showWorkspaceSelector,
    setShowWorkspaceSelector: handleToggleWorkspaceSelector,
    verifyPin: handleVerifyPin,
    setCurrentWorkspace: handleSetWorkspace,
    clearWorkspace: handleClearWorkspace,
    refreshSession: handleRefreshSession,
    createWorkspace: handleCreateWorkspace,
    getWorkspaces: handleGetWorkspaces,
  }), [
    state,
    isLoadingWorkspaces,
    workspaceError,
    handleVerifyPin,
    handleSetWorkspace,
    handleClearWorkspace,
    handleRefreshSession,
    handleToggleWorkspaceSelector,
    handleCreateWorkspace,
    handleGetWorkspaces
  ]);

  return (
    <WorkspaceContext.Provider value={contextValue}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace(): WorkspaceContextType {
  const context = useContext(WorkspaceContext);

  if (!context) {
    throw new Error(
      "useWorkspace must be used within a WorkspaceProvider. " +
      "Please ensure the component is wrapped in a WorkspaceProvider."
    );
  }

  return context;
}

export function isValidSession(session: unknown): session is WorkspaceSession {
  if (!session || typeof session !== "object") return false;

  const typedSession = session as WorkspaceSession;

  return (
    typeof typedSession.workspaceId === "string" &&
    typedSession.workspaceId.trim().length > 0 &&
    typeof typedSession.expiresAt === "number" &&
    typedSession.expiresAt > 0 &&
    typeof typedSession.lastActivityAt === "number" &&
    typedSession.lastActivityAt > 0 &&
    typedSession.lastActivityAt <= Date.now()
  );
}